import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import type { HttpRequestCancelToken } from '@core/HttpRequestCancelToken'
import type { ITask, TaskInfo } from '@core/transmission/ITask'
import { TaskState, TaskError, TaskErrorTipsMap, DeviceTaskError, TransmissionTaskType } from '@core/transmission/ITask'
import { v4 as uuidV4 } from 'uuid'
import { TaskScheduler } from './TaskScheduler'
import type { IDevice } from '../devices/IDevice'
import { debounce, isEmpty } from 'lodash-es'
import { TransmissionDataBusProxy } from './TransmissionDataBusProxy'
import { NoticeOperationResult } from '@core/databus/IDataSyncManager'
import { useOtrack } from '@core/compositions/Otrack'
import type { TransmissionTrackInfo } from '../TrackConstants'
import { TrackEventMap } from '../TrackConstants'
import { formatBytes, isInIosAPP } from '../Utils'
import type { TransmissionNoticeData } from '../databus/DataSyncManager'
import { i18n } from '@/i18n'

const { t } = i18n.global

export const ILLEGAL_FILE_SIZE = -1
const TASK_STATE_SYNC_IN_MS = 3 * 60 * 1000
const LAN_UPLOAD_TASK_STATE_SYNC_IN_MS = 10 * 60 * 1000

export const SUCCESS_IN_PERCENT = 100

const { track } = useOtrack()

export abstract class AbsTask implements ITask {
  id: string = uuidV4()
  sequenceId: number = TaskScheduler.getSequenceId()
  protected _deviceId: string
  protected _appId = ''
  protected _noticeId = ''
  protected _state: TaskState = TaskState.IDLE //当前传输任务状态
  protected _transmitted = ILLEGAL_FILE_SIZE //已传输文件大小
  protected _fileSize = ILLEGAL_FILE_SIZE //传输文件大小
  protected _fileName = '' //传输文件名
  protected _requestCancelToken: HttpRequestCancelToken | undefined
  protected _taskScheduler: TaskScheduler<AbsTask> | undefined
  protected _isViaLan: boolean
  protected _failedReason: TaskError | DeviceTaskError | undefined = undefined //错误类型
  protected _preTransmissionNotice = ''
  protected _numberOfRetries = 0
  protected abstract _transmissionTaskType: TransmissionTaskType

  canChangeState = true //是否可以修改任务状态
  #stateSyncTimerNum: ReturnType<typeof setTimeout> | undefined = undefined
  #isDeleted = false
  constructor(deviceId: string, isViaLan: boolean, appId: string) {
    this._deviceId = deviceId
    this._isViaLan = isViaLan
    this._appId = appId
  }

  setTaskScheduler(listener: TaskScheduler<AbsTask>): void {
    this._taskScheduler = listener
  }

  toJson(): TaskInfo {
    const failedReasonMsg = (this._failedReason && TaskErrorTipsMap.get(this._failedReason)) ?? ''
    let failedReasonTips = ''

    if (this.failedReason === DeviceTaskError.DEVICE_HAVE_NOT_ENOUGH_SPACE_AVAILABLE) {
      failedReasonTips = t(failedReasonMsg, { deviceName: this._getDevice()?.model ?? '' })
    } else {
      failedReasonTips = t(failedReasonMsg, { default: failedReasonMsg })
    }

    return {
      id: this.id,
      fileName: this._fileName,
      fileSize: this._fileSize,
      state: this._state,
      sourceDeviceId: this._deviceId,
      completionRatio: 0,
      isViaLan: this._isViaLan,
      appId: this._appId,
      failedReason: this._failedReason,
      canChangeState: this.canChangeState,
      transmissionTaskType: this._transmissionTaskType,
      failedReasonTips,
    }
  }

  get sourceDevice(): string {
    const device = DeviceManager.getDeviceById(this._deviceId)

    return device ? device.model : ''
  }

  get state(): TaskState {
    return this._state
  }

  set state(state: TaskState) {
    this._state = state
  }

  set noticeId(id: string) {
    this._noticeId = id
  }

  get noticeId(): string {
    return this._noticeId
  }

  get failedReason(): TaskError | DeviceTaskError | undefined {
    return this._failedReason
  }

  start(): Promise<void> {
    throw new Error('Method not implemented.')
  }

  pause(): Promise<void> {
    throw new Error('Method not implemented.')
  }

  resume(): Promise<void> {
    throw new Error('Method not implemented.')
  }

  onRemove(needCallService?: boolean): Promise<boolean> {
    throw new Error('Method not implemented.')
  }

  updateInfo(transmissionNotice: TransmissionNoticeData, isFreezed?: boolean): void {
    throw new Error('Method not implemented.')
  }

  track() {
    track<TransmissionTrackInfo>(
      { eventId: TrackEventMap.TRANSMISSION_TASK },
      {
        transmission_state: this._state === TaskState.SUCCESS ? 'success' : 'failed',
        transmission_type: this._transmissionTaskType,
        transmission_network_type: this._isViaLan ? 'LAN' : 'WAN',
        transmission_file_size: formatBytes(this._fileSize),
        number_of_retries: this._numberOfRetries++,
        reason_for_failure: this._state === TaskState.SUCCESS ? '' : this.failedReason ?? '',
        is_in_ios_app: isInIosAPP(),
      }
    )
  }

  setTimer() {
    if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(this.state)) return

    console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, '刷新计时')
    this.clearTimer()
    this.#stateSyncTimerNum = setTimeout(
      () => {
        if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(this.state)) return

        this._failedReason = TaskError.TASK_STATE_SYNC_TIMEOUT
        this._taskScheduler?.setTaskError(this)
      },
      // 局域网环境下，上传完成后，端侧会进行md5计算，导致没有任何进度更新，导致超时，需要延长计时
      this._transmissionTaskType === TransmissionTaskType.UPLOAD && this._isViaLan
        ? LAN_UPLOAD_TASK_STATE_SYNC_IN_MS
        : TASK_STATE_SYNC_IN_MS
    )
  }

  clearTimer() {
    if (this.#stateSyncTimerNum !== undefined) {
      clearTimeout(this.#stateSyncTimerNum)
      this.#stateSyncTimerNum = undefined
    }
  }

  get isDeleted(): boolean {
    return this.#isDeleted
  }

  protected markAsDeleted(): void {
    this.#isDeleted = true
  }

  protected _getDevice(): IDevice | undefined {
    return DeviceManager.getDeviceById(this._deviceId)
  }

  protected _terminateDueToDeviceNotFound(): void {
    this._failedReason = TaskError.DEVICE_NOT_FOUND
    this._taskScheduler?.setTaskError(this)
  }

  protected _getCompletionRation(): number {
    return 0
  }

  protected _checkLatestStatus = debounce(async () => {
    if (!isEmpty(this._noticeId)) {
      const result = await TransmissionDataBusProxy.getInstance().getNoticeById(this._noticeId)

      if (result.status === NoticeOperationResult.SUCCESS && result.data) {
        const message = result.data
        const MessageStr = JSON.stringify(message)
        const isFreezed = isEmpty(this._preTransmissionNotice) || this._preTransmissionNotice === MessageStr

        console.log(
          `[Transmission]:_checkLatestStatus: ${this._fileName} getNoticeById  ${new Date().toLocaleString()} `,
          message,
          'isFreezed:',
          isFreezed
        )

        this.updateInfo(message, isFreezed)
        this._preTransmissionNotice = MessageStr
      } else {
        console.log(
          `[Transmission]:_checkLatestStatus: ${this._fileName} getNoticeById failed ${new Date().toLocaleString()} `,
          result
        )
      }
    } else {
      console.log(
        `[Transmission]:_checkLatestStatus: ${
          this._fileName
        } failed noticeId is undefined ${new Date().toLocaleString()} `
      )
    }
  }, 30 * 1000)
}
